import { findProformGlassPreset, PROFORM_GLASS_PRESETS } from './proform-glass-presets';
import { resolveProformGlassMaterial } from './proform-glass-material';

export type ProformGlassBackground = 'none' | 'logo' | 'flowers';

export type ProformGlassPreviewSelection = {
  groupCode?: string;
  fillingCode?: string;
  orientation?: string;
  ornamentSide?: string;
  color?: string;
};

const glassPreviewCopy = {
  cs: {
    background: 'Pozadí za sklem',
    backgroundError: 'Pozadí se nepodařilo načíst. Náhled skla zůstává dostupný.',
    backgrounds: { none: 'Bez pozadí', logo: 'Logo', flowers: 'Květ' },
    clearGlass: 'Čiré sklo',
    chooseFilling: 'Pro náhled skla vyberte Skupinu výplní a Výplň v konfigurátoru.',
    genericGlass: 'Orientační sklo',
    illustrativeNote: 'Ilustrační náhled materiálu. Pozadí slouží pouze pro porovnání průhledu.',
    testGlass: 'Test skla',
    referenceNeeded: 'Orientační vzhled; přesný vzor nebo odstín musí potvrdit dodavatel.',
    customGlass: 'Jiné sklo: vzhled není určen. Zobrazeno pouze orientační čiré sklo.',
    colorNeeded:
      'Odstín není dostupný. Vyberte specifikaci barvy; zatím je zobrazen neutrální lak.',
    opaqueNote: 'Neprůhledná výplň. Pozadí přes ni není vidět.',
    mirrorNote: 'Orientační zrcadlo se studiovým odrazem; neodráží zvolené pozadí za dvířky.',
  },
  en: {
    background: 'Background behind glass',
    backgroundError: 'The background could not be loaded. The glass preview is still available.',
    backgrounds: { none: 'None', logo: 'Logo', flowers: 'Flower' },
    clearGlass: 'Clear glass',
    chooseFilling: 'Select a filling group and filling in the configurator to preview the glass.',
    genericGlass: 'Illustrative glass',
    illustrativeNote:
      'Illustrative material preview. The background only helps compare visibility.',
    testGlass: 'Glass test',
    referenceNeeded:
      'Approximate appearance; the supplier must confirm the exact pattern or colour.',
    customGlass: 'Other glass: appearance is unspecified. Only illustrative clear glass is shown.',
    colorNeeded:
      'Colour unavailable. Select a colour specification; neutral lacquer is shown for now.',
    opaqueNote: 'Opaque filling. The background is not visible through it.',
    mirrorNote:
      'Illustrative mirror with studio reflections, not a reflection of the backdrop behind the door.',
  },
  sk: {
    background: 'Pozadie za sklom',
    backgroundError: 'Pozadie sa nepodarilo načítať. Náhľad skla zostáva dostupný.',
    backgrounds: { none: 'Bez pozadia', logo: 'Logo', flowers: 'Kvet' },
    clearGlass: 'Číre sklo',
    chooseFilling: 'Pre náhľad skla vyberte Skupinu výplní a Výplň v konfigurátore.',
    genericGlass: 'Orientačné sklo',
    illustrativeNote: 'Ilustračný náhľad materiálu. Pozadie slúži iba na porovnanie priehľadu.',
    testGlass: 'Test skla',
    referenceNeeded: 'Orientačný vzhľad; presný vzor alebo odtieň musí potvrdiť dodávateľ.',
    customGlass: 'Iné sklo: vzhľad nie je určený. Zobrazené je iba orientačné číre sklo.',
    colorNeeded:
      'Odtieň nie je dostupný. Vyberte špecifikáciu farby; zatiaľ je zobrazený neutrálny lak.',
    opaqueNote: 'Nepriehľadná výplň. Pozadie cez ňu nie je vidieť.',
    mirrorNote: 'Orientačné zrkadlo so štúdiovým odrazom; neodráža zvolené pozadie za dvierkami.',
  },
} as const;

export function getProformGlassPreviewCopy(locale: string) {
  return glassPreviewCopy[locale as keyof typeof glassPreviewCopy] ?? glassPreviewCopy.en;
}

export function getProformGlassMaterialLabel(
  locale: string,
  selection: ProformGlassPreviewSelection,
) {
  return (
    findProformGlassPreset(selection.groupCode, selection.fillingCode)?.label ??
    getProformGlassPreviewCopy(locale).genericGlass
  );
}

export function getProformGlassMaterialNote(
  locale: string,
  selection: ProformGlassPreviewSelection,
) {
  const copy = getProformGlassPreviewCopy(locale);
  const material = resolveProformGlassMaterial(selection);
  if (material.warning === 'custom-glass') return copy.customGlass;
  if (material.warning === 'color-needed') return copy.colorNeeded;
  if (material.warning === 'reference-needed') return copy.referenceNeeded;
  if (material.family === 'mirror') return copy.mirrorNote;
  if (material.family === 'lacquer') return copy.opaqueNote;
  return copy.illustrativeNote;
}

export function normalizeProformGlassBackground(value?: string): ProformGlassBackground {
  return value === 'none' || value === 'flowers' ? value : 'logo';
}

/** Sandbox shares the registry; product selection requires the exact group/code pair. */
export function getW030GlassTestSelection(code?: string): ProformGlassPreviewSelection | undefined {
  const preset = PROFORM_GLASS_PRESETS.find(
    item => item.fillingCode === code?.trim().toUpperCase(),
  );
  return preset ? { groupCode: preset.groupCode, fillingCode: preset.fillingCode } : undefined;
}
